import { useMutation, useQuery } from '@tanstack/react-query'

import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type { IUserAdmin, IUserPublic } from '~/types/user'

import {
  addGroups,
  addRoles,
  createUsers,
  deleteGroups,
  deleteRoles,
  getUserAsAdmin,
  getUsersAsAdmin,
  updateUserEmail,
  updateUserName,
} from '~/api/mwm-users'

////////// KEYS //////////

export const keys = {
  all: ['mwm-users'] as const,
  details: (userId?: string) => [...keys.all, 'details', userId] as const,
  list: () => [...keys.all, 'list'] as const,
}

////////// QUERIES //////////

export const useMwmUsersListQuery = (): UseQueryResult<IPaginatedResponse<IUserAdmin>> => {
  return useQuery({ queryFn: getUsersAsAdmin, queryKey: keys.list() })
}

export const useMwmUsersDetailsQuery = (userId?: string): UseQueryResult<IUserAdmin> => {
  return useQuery({
    enabled: !!userId,
    queryFn: () => getUserAsAdmin(userId as string),
    queryKey: keys.details(userId),
  })
}

////////// MUTATIONS //////////

export const useCreateUsersMutation = (): UseMutationResult<
  IUserPublic[],
  unknown,
  { email: string; name: string }[]
> => {
  return useMutation({ mutationFn: createUsers })
}

export const useUpdateUserEmailMutation = (): UseMutationResult<
  IUserPublic,
  unknown,
  { email: string; userId: string }
> => {
  return useMutation({ mutationFn: updateUserEmail })
}

export const useUpdateUserNameMutation = (): UseMutationResult<
  IUserPublic,
  unknown,
  { name: string; userId: string }
> => {
  return useMutation({ mutationFn: updateUserName })
}

export const useAddGroupsToUserMutation = (): UseMutationResult<
  IUserAdmin,
  unknown,
  { groupIds: string[]; userId: string }
> => {
  return useMutation({ mutationFn: addGroups })
}

export const useAddRolesToUserMutation = (): UseMutationResult<
  IUserAdmin,
  unknown,
  { roleIds: string[]; userId: string }
> => {
  return useMutation({ mutationFn: addRoles })
}

export const useDeleteGroupsFromUserMutation = (): UseMutationResult<
  IUserAdmin,
  unknown,
  { groupIds: string[]; userId: string }
> => {
  return useMutation({ mutationFn: deleteGroups })
}

export const useDeleteRolesFromUserMutation = (): UseMutationResult<
  IUserAdmin,
  unknown,
  { roleIds: string[]; userId: string }
> => {
  return useMutation({ mutationFn: deleteRoles })
}
